import React from 'react';
import { Metafields, Product, ProductTypes } from '@/types/ecommerce.types';
import { withQuery } from '../data/withQuery';
import {
  getCornerImage,
  getCurrentVariant,
  getMetafieldImage,
  getMetafieldV2,
  handleizeProductType,
  isOrnamentProduct,
} from '@/utils/utils';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/store';
import { resetGalleryItem } from '@/store/product/productSlice';
import { setShouldCropToAll } from '@/store/upload/uploadSlice';
import { navigate } from '@reach/router';
import { ProductOptionsTitle } from './ProductOptions.styles';
import styled from 'styled-components';
import { colors } from '@/themes/colorsMapping';
import { getSortDictionary } from './getSortDictionary';
import { getPartner } from '@/utils/partner';
import { useVariantTiles } from '@/hooks/useVariantTiles';
import { useQuery } from '@apollo/client';
import { GET_PRODUCT_BY_HANDLE } from '../data/productQuery';
import PriceDisplay from '../shared/PriceDisplay';
import { ProductVariantEdge } from 'shopify-storefront-api-typings';

type Variant = {
  id: string;
  handle: string;
  image?: string | null;
  mat_group_title?: string | null;
  mat_type_label?: string | null;
  variant?: ProductVariantEdge;
};

const MatWrapper = styled.div`
  margin-top: 20px;
  margin-bottom: 0;
  @media (min-width: 800px) {
    margin-bottom: 30px;
  }

  ul {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
    margin-left: 0;

    padding: 3px;
    margin-top: 13px;
    position: relative;
    list-style-type: none;
    flex-wrap: wrap;
  }

  li {
    border-radius: 10px;
    overflow: hidden;
    border: 1px solid ${colors.darkGray};
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    &.active {
      border-color: var(--color-primary);
      box-shadow: 0 0 0 3px var(--color-primary-20);

      @media (max-width: 800px) {
        color: var(--color-primary);
      }
    }

    label {
      padding: 8px !important;
      padding-left: 10px !important;
      padding-right: 10px !important;
      margin: 0 !important;
      display: block;
      font-size: 16px;
      font-weight: 500;
      border: 0 !important;
      text-align: center;
      width: 100%;
    }
    img + label {
      margin-top: -10px !important;
      text-align: left;
    }
  }

  img {
    width: 100%;
    height: 70px;
    object-position: bottom;
    object-fit: cover;
  }
`;

type PropsTypes = {
  product: Product;
  shopMetafields: Metafields;
};

const MatImageOptions = ({ product, shopMetafields }: PropsTypes) => {
  const dispatch = useDispatch();
  const partner = getPartner();
  const tile = useSelector((state: RootState) => state.upload.tiles[0]);
  const { type } = useSelector((state: RootState) => state.product);
  const productType = handleizeProductType(product?.productType);
  const isGallery = productType === ProductTypes.GALLERY;
  const variants = product?.mat_variants?.references?.edges.map((edge: any) => edge?.node);
  const tilesData = getMetafieldV2('tile', product?.metafields || []);
  const firstTileHandle = tilesData ? JSON.parse(tilesData)[0]?.split(':')?.[0] : null;
  const { data } = useQuery(GET_PRODUCT_BY_HANDLE, {
    variables: {
      handle: firstTileHandle,
    },
  });
  const tileData = data?.productByHandle;
  const { tileCornerImages } = useVariantTiles(variants);

  if (!product) return null;

  const currentVariantFromPageProduct = getCurrentVariant(product?.variants?.edges, null, {
    checkParams: true,
  });
  const frameSize = currentVariantFromPageProduct?.selectedOptions[0]?.value;
  const currentProductMatType = getMetafieldV2('mat_type_label', product.metafields);
  const matGroupTitle = getMetafieldV2('mat_group_title', product.metafields);
  let matProductGroup: Variant[] = [];

  // TODO MEM-1247 remove after updates START
  const currentProduct = getMetafieldV2('mat_type', product.metafields);
  if (variants?.length) {
    matProductGroup = variants?.map((name: any) => ({
      id: name?.id,
      handle: name?.handle,
      mat_type_label: name?.mat_type?.value,
      image:
        getMetafieldImage(name.mat_image, name.images.edges) ??
        tileCornerImages.find((el: any) => el.handle === name.handle)?.image,
      variant: name?.variants?.edges?.[0],
    }));
    const sort = getSortDictionary(getMetafieldV2('mat_toggle_order', shopMetafields));
    if (currentProduct && matProductGroup) {
      matProductGroup.push({
        id: product.id,
        handle: product.handle,
        image: getMetafieldImage(product.mat_image, product.images.edges),
        mat_type_label: currentProduct,
        variant: product?.variants?.edges?.[0],
      });
    }
    matProductGroup.sort(
      (a, b) =>
        sort[a?.mat_type_label?.toLowerCase() ?? ''] - sort[b?.mat_type_label?.toLowerCase() ?? '']
    );
  }
  // TODO MEM-1247 remove after updates END
  if (product.mat_product_group?.references?.edges?.length) {
    // TODO MEM-1247 remove condition
    matProductGroup = product.mat_product_group?.references?.edges.map((el: any) => ({
      id: el.node.id,
      handle: el.node.handle,
      image: getMetafieldImage(el.node.mat_image, el.node.images.edges),
      mat_group_title: el.node.mat_group_title?.value,
      mat_type_label: el.node.mat_type_label?.value,
      variant: el.node.variants?.edges?.[0],
    }));

    if (!matProductGroup.some((el: Variant) => el.id === product.id)) {
      matProductGroup.unshift({
        id: product.id,
        handle: product.handle,
        image: getMetafieldImage(product.mat_image, product.images.edges),
        mat_group_title: matGroupTitle,
        mat_type_label: currentProductMatType,
        variant: product?.variants?.edges?.[0],
      });
    }
  }

  if (isGallery) {
    matProductGroup = matProductGroup.map((el: any) => ({
      ...el,
      image:
        tileCornerImages.find((tile: any) => tile.matType === el.mat_type_label)?.image ??
        getCornerImage(tileData?.images?.edges),
    }));
  }

  if (!matProductGroup?.length) {
    return null;
  }

  return (
    <MatWrapper>
      <ProductOptionsTitle>{matGroupTitle || 'Mat'}: </ProductOptionsTitle>
      <ul>
        {matProductGroup.map(({ handle, mat_type_label, image, variant }) => (
          <li
            className={handle === product.handle ? 'active' : ''}
            key={handle}
            onClick={() => {
              if (!handle) return;
              dispatch(resetGalleryItem());
              if (tile) {
                dispatch(setShouldCropToAll());
              }

              const searchParams = new URLSearchParams();
              searchParams.set('size', frameSize as string);
              if (partner) {
                searchParams.set('partner', partner);
              }

              const url = `/products/${handle}?${searchParams.toString()}`;

              window.scrollTo({ top: 0, behavior: 'smooth' });
              setTimeout(() => {
                navigate(url);
              }, 250);
            }}
          >
            {handle === product.handle && (
              <input
                type="radio"
                checked={true}
                onChange={() => {}}
                className="fn-variant-input tmplt-product__variants-input visually-hidden"
              />
            )}
            {image && <img src={image} alt="" />}
            <label>
              <span>{mat_type_label}</span>
              {isOrnamentProduct(type, product?.title) && isGallery && (
                <div>
                  <PriceDisplay product={product} variant={variant} />
                </div>
              )}
            </label>
          </li>
        ))}
      </ul>
    </MatWrapper>
  );
};
export default withQuery(MatImageOptions);
